import { Menu, Row } from 'antd';
import { Collapse, Drawer } from 'antd5';
import React, { useCallback, useState } from 'react';
import CloseRounded from '@material-ui/icons/CloseRounded';
import styled from '@emotion/styled';
import { useSelector } from 'react-redux';
import MenuRounded from '@material-ui/icons/MenuRounded';
import { filter } from 'lodash';

import { HeaderImageContainer, HeaderSection, ImageWrapper, RightContainer, StyledButton } from './header-styles';
import { Link18 } from './link';

import { LanguageSelector, useI18NContext } from '../../i18n';
import { Button } from '../';
import { IPublicMenuItem } from '../../types/header-menu-item';
import { isAuthenticated } from '../../state/selectors/auth';
import theme from '../../theme';
import useLogos from '../../hooks/use-logos';

const StyledDrawer = styled(Drawer)`
  & button:first-of-type {
    margin-right: 24px;
  }

  & .ant-collapse:first-of-type {
    margin-top: 8px;
  }
`;

const StyledCollapse = styled(Collapse)`
  background-color: ${theme.colorsWhite};
  border: none;
`;

const MenuItem = styled(Button)`
  &.ant-btn {
    margin: 16px 0 0 16px;
    background-color: ${theme.colorsWhite};
    color: ${theme.colorsBlack} !important;
    font-weight: bold !important;
    font-size: 18px !important;
    display: block;
    border: 0;
    cursor: pointer;

    span {
      text-decoration: none !important;
    }
  }
`;
const StyledPanel = styled(Collapse.Panel)`
  border-bottom: none !important;

  .ant-collapse-header {
    color: ${theme.colorsBlack} !important;
    font-weight: bold !important;
    font-size: 18px !important;
  }
`;
const StyledMenu = styled(Menu)`
  border: none;
`;

const MenuGroupItem = styled(Menu.Item)`
  color: ${theme.colorsBlack} !important;
  font-size: 16px !important;

  &.ant-menu-item-selected {
    color: ${theme.colorsPrimaryBlue} !important;
    font-weight: bold !important;
    background-color: ${theme.colorsWhite} !important;
  }
`;
const MenuIcon = styled(MenuRounded)`
  margin-right: 8px;
  cursor: pointer;
`;

const CloseIcon = styled(CloseRounded)`
  font-size: 32px !important;
  cursor: pointer;
`;

const AuthWrapper = styled.div`
  position: fixed;
  bottom: 32px;

  a:first-of-type {
    margin-right: 24px;
  }
`;

const maskStyle = { backgroundColor: 'rgba(0, 0, 0, 0.8)' };
const headerStyle = { padding: 0, paddingTop: 'env(safe-area-inset-top)', border: 'none', borderRadius: 0 };

type UserHeaderMobileNavProps = {
  menu: IPublicMenuItem[];
  handleClick: (link: string) => void;
};

const UserHeaderMobileNav = React.memo(function UserHeaderMobileNav({ menu, handleClick }: UserHeaderMobileNavProps) {
  const loggedIn = useSelector(isAuthenticated);
  const [isExpanded, setIsExpanded] = useState(false);
  const handleMenuClick = useCallback(() => {
    setIsExpanded(!isExpanded);
  }, [isExpanded]);
  const handleCloseClick = useCallback(() => {
    setIsExpanded(false);
  }, []);
  const { translate, translateSlug } = useI18NContext();

  const buttons = filter(menu, (item) => {
    return item.is_button;
  });
  const menuItems = filter(menu, (item) => {
    return !item.is_button;
  });

  const { tenantLogoUrl } = useLogos();
  return (
    <>
      <MenuIcon titleAccess="toggle menu" onClick={handleMenuClick} />
      <StyledDrawer
        title={
          <HeaderSection align="middle" justify="space-between">
            <ImageWrapper>{tenantLogoUrl && <HeaderImageContainer src={tenantLogoUrl} />}</ImageWrapper>
            <RightContainer>
              <CloseIcon titleAccess="close menu" onClick={handleCloseClick} />
            </RightContainer>
          </HeaderSection>
        }
        placement="right"
        closable={false}
        onClose={handleCloseClick}
        open={isExpanded}
        styles={{ mask: maskStyle, header: headerStyle }}
        width="90%"
      >
        <Row align="middle" justify="space-between">
          <div>
            {buttons &&
              buttons.map((button) => {
                return (
                  <StyledButton
                    key={button.navigation_group_name}
                    type="primary-blue"
                    onClick={() => handleClick(translateSlug(button.navigation_group_slug))}
                  >
                    {translate(button).display_name}
                  </StyledButton>
                );
              })}
          </div>
          <LanguageSelector />
        </Row>
        {loggedIn && (
          <MenuItem key="dashboard" type="link" onClick={() => handleClick('/myhsf/dashboard')}>
            My Dashboard
          </MenuItem>
        )}
        {menuItems &&
          menuItems.map((item) => {
            if (item.navigation_group_items.length === 0) {
              return (
                <MenuItem
                  key={item.navigation_group_name}
                  type="link"
                  onClick={() => handleClick(item.navigation_group_slug)}
                >
                  {translate(item).display_name}
                </MenuItem>
              );
            } else {
              const subMenuItems = item.navigation_group_items.map((item) => {
                return (
                  <MenuGroupItem key={item.navigation_item_name} onClick={() => handleClick(item.navigation_item_slug)}>
                    {translate(item).display_name}
                  </MenuGroupItem>
                );
              });
              return (
                <StyledCollapse bordered={false} expandIconPosition="start" key={item.navigation_group_name}>
                  <StyledPanel key={item.navigation_group_name} header={translate(item).display_name}>
                    <StyledMenu>{subMenuItems}</StyledMenu>
                  </StyledPanel>
                </StyledCollapse>
              );
            }
          })}

        {!loggedIn && (
          <AuthWrapper>
            <Link18 to="/login">Sign In</Link18>
            <Link18 to="/sign-up">Sign Up</Link18>
          </AuthWrapper>
        )}
      </StyledDrawer>
    </>
  );
});

export default UserHeaderMobileNav;
